import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { Archive, FolderArchive, PlayCircle, ExternalLink, Undo2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Archives() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null);
  const [payload, setPayload] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/archives/years").then((r) => {
      setYears(r.data.years || []);
      if ((r.data.years || []).length && !year) setYear(r.data.years[0]);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!year) return;
    api.get(`/archives/${year}`).then((r) => setPayload(r.data));
  }, [year]);

  const runFor = async (yr) => {
    if (!window.confirm(`Archive every Closed deal for ${yr} whose required compliance is approved?`)) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/archives/run?year=${yr}`);
      toast.success(`Archived ${data.archived} · Skipped ${data.skipped_pending_compliance} pending`);
      // Refresh
      const yrs = await api.get("/archives/years");
      setYears(yrs.data.years || []);
      setYear(yr);
    } catch { toast.error("Archive run failed"); }
    finally { setRunning(false); }
  };

  const thisYear = new Date().getFullYear();
  const prevYear = thisYear - 1;

  return (
    <div className="space-y-8" data-testid="archives-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Brokerage · Preserved Records</div>
          <h1 className="font-heading text-5xl mt-2">Broker Archives</h1>
          <p className="text-sm text-[#050303]/60 mt-2 max-w-2xl">
            Closed transactions whose required compliance is fully approved are auto-archived on January 1 each year. Documents and commission data stay preserved here forever — the active Pipeline and Compliance list stay clean.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => runFor(prevYear)}
            disabled={running}
            className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="run-archive-btn"
          >
            <PlayCircle size={16} /> {running ? "Archiving…" : `Archive ${prevYear} now`}
          </button>
        )}
      </header>

      {years.length === 0 ? (
        <section className="core-card p-10 text-center">
          <FolderArchive size={40} strokeWidth={1.4} className="mx-auto text-[#bf858e]" />
          <div className="font-heading text-xl mt-3">No archives yet</div>
          <p className="text-sm text-[#050303]/60 mt-1 max-w-md mx-auto">
            The scheduler will auto-archive prior-year deals every January 1. {isAdmin ? "Or run it now with the button above." : "Ask your broker to run the year-end archive."}
          </p>
        </section>
      ) : (
        <>
          <section className="flex gap-2 flex-wrap">
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`pill-btn text-sm ${year === y ? "bg-[#050303] text-white" : "bg-white border border-[#d1cece] hover:border-[#bf858e]"}`}
                data-testid={`year-tab-${y}`}
              >
                {y} Broker Archives
              </button>
            ))}
          </section>

          {payload && (
            <>
              <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard label="Archived Deals" value={payload.totals.count} />
                <StatCard label="Total Volume" value={fmtMoney(payload.totals.volume)} />
                <StatCard label="Total GCI" value={fmtMoney(payload.totals.gci)} />
              </section>

              <section className="core-card p-0 overflow-x-auto" data-testid="archived-deals-list">
                <table className="w-full text-sm">
                  <thead className="bg-[#e1b7c1]/40 text-[10px] uppercase tracking-wider text-[#050303]/60">
                    <tr>
                      <th className="text-left px-4 py-3">Property</th>
                      <th className="text-left px-4 py-3">Address</th>
                      <th className="text-left px-4 py-3">Closed</th>
                      {isAdmin && <th className="text-left px-4 py-3">Agent</th>}
                      <th className="text-right px-4 py-3">Sale Price</th>
                      <th className="text-right px-4 py-3">GCI</th>
                      <th className="text-right px-4 py-3">Docs</th>
                      {isAdmin && <th className="text-right px-4 py-3">Restore</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#d1cece]">
                    {payload.deals.map((d) => (
                      <tr key={d.id} data-testid={`archived-deal-${d.id}`}>
                        <td className="px-4 py-3 font-semibold">{d.title}</td>
                        <td className="px-4 py-3 text-[#050303]/70">{d.address}</td>
                        <td className="px-4 py-3 text-[#050303]/70">{d.close_date || "—"}</td>
                        {isAdmin && <td className="px-4 py-3 text-[#050303]/70">{d.agent_name || ""}</td>}
                        <td className="px-4 py-3 text-right">{fmtMoney(d.price)}</td>
                        <td className="px-4 py-3 text-right">{fmtMoney(((d.price || 0) * (d.commission_pct || 0)) / 100)}</td>
                        <td className="px-4 py-3 text-right">
                          <ArchivedDealDocsButton year={year} dealId={d.id} />
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-right">
                            <UnarchiveButton year={year} deal={d} onDone={async () => {
                              const yrs = await api.get("/archives/years");
                              setYears(yrs.data.years || []);
                              if (yrs.data.years?.includes(year)) {
                                const r = await api.get(`/archives/${year}`);
                                setPayload(r.data);
                              } else {
                                setYear(yrs.data.years?.[0] || null);
                              }
                            }} />
                          </td>
                        )}
                      </tr>
                    ))}
                    {payload.deals.length === 0 && (
                      <tr><td colSpan={isAdmin ? 8 : 6} className="px-4 py-8 text-center text-[#050303]/50">No deals in this year&apos;s archive.</td></tr>
                    )}
                  </tbody>
                </table>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="core-card p-5">
      <div className="overline">{label}</div>
      <div className="font-heading text-3xl mt-2">{value}</div>
    </div>
  );
}

function ArchivedDealDocsButton({ year, dealId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    if (!items) {
      try {
        const { data } = await api.get(`/archives/${year}/deals/${dealId}/items`);
        setItems(data.items || []);
      } catch { toast.error("Could not load documents"); return; }
    }
    setOpen(true);
  };

  return (
    <>
      <button onClick={toggle} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303]"
        data-testid={`docs-toggle-${dealId}`}>
        <Archive size={12} className="inline mr-1" /> {open ? "Hide" : "View"}
      </button>
      {open && items && (
        <div className="mt-2 text-left space-y-1 text-xs">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 py-1">
              <span className="truncate flex-1">{it.title}</span>
              {it.document?.url ? (
                <a href={it.document.url} target="_blank" rel="noreferrer"
                  className="text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
                  data-testid={`doc-link-${it.id}`}>
                  <ExternalLink size={11} /> Open
                </a>
              ) : (
                <span className="text-[#050303]/40">no file</span>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="text-[#050303]/50">No compliance items linked.</div>}
        </div>
      )}
    </>
  );
}


function UnarchiveButton({ year, deal, onDone }) {
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    if (!window.confirm(`Restore "${deal.title}" back to the active Deal Pipeline?`)) return;
    setBusy(true);
    try {
      await api.post(`/archives/${year}/deals/${deal.id}/unarchive`);
      toast.success(`Restored ${deal.title}`);
      await onDone?.();
    } catch { toast.error("Could not restore deal"); }
    finally { setBusy(false); }
  };
  return (
    <button
      onClick={restore}
      disabled={busy}
      className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 ml-auto"
      data-testid={`restore-${deal.id}`}
    >
      <Undo2 size={12} /> {busy ? "Restoring…" : "Restore"}
    </button>
  );
}
